import { Component, inject, ApplicationRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { SwUpdate } from '@angular/service-worker';
import { Auth } from '@angular/fire/auth';
import { concat, firstValueFrom, interval } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';
import { waitForAuthUser } from '../utils/wait-for-auth';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [IonApp, IonRouterOutlet]
})
export class AppComponent {
  private swUpdate = inject(SwUpdate, { optional: true });
  private appRef = inject(ApplicationRef);
  private destroyRef = inject(DestroyRef);
  private auth = inject(Auth);
  private router = inject(Router);

  constructor() {
    void this.hideSplashAfterAuth();

    if (this.swUpdate?.isEnabled) {
      const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
      const everySixHours$ = interval(6 * 60 * 60 * 1000);
      const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

      everySixHoursOnceAppIsStable$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.swUpdate!.checkForUpdate());

      this.swUpdate.versionUpdates
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          if (event.type === 'VERSION_READY') {
            window.location.reload();
          }
        });
    }
  }

  private async hideSplashAfterAuth(): Promise<void> {
    try {
      await Promise.race([
        waitForAuthUser(this.auth),
        new Promise<void>((resolve) => setTimeout(resolve, 4000)),
      ]);
    } catch {
      // Show the app even if auth restore fails.
    }

    try {
      await Promise.race([
        this.waitForFirstNavigation(),
        new Promise<void>((resolve) => setTimeout(resolve, 4000)),
      ]);
    } catch {
      // Fall through and hide the splash.
    }

    const hideSplash = (window as Window & { hideSplash?: () => void }).hideSplash;
    hideSplash?.();
  }

  private waitForFirstNavigation(): Promise<void> {
    const url = this.router.url;
    if (url && url !== '/') {
      return Promise.resolve();
    }
    return firstValueFrom(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        take(1)
      )
    ).then(() => undefined);
  }
}